<template>
  <div>
    <!-- Stat cards -->
    <div class="row g-3 mb-3">
      <div class="col-12 col-md-6 col-xl-3">
        <div class="card stat-card h-100">
          <div class="card-body">
            <div class="stat-head">
              <span class="stat-title">
                <i class="fa-solid fa-user-plus text-muted me-2"></i>Cadastros totais
              </span>
              <span class="stat-period">desde o início</span>
            </div>
            <div class="stat-split">
              <div>
                <div class="stat-value">{{ numberBR(data.total_registrations.patient) }}</div>
                <div class="stat-label">Pacientes</div>
              </div>
              <div>
                <div class="stat-value">{{ numberBR(data.total_registrations.psy) }}</div>
                <div class="stat-label">Psis</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 col-md-6 col-xl-3">
        <div class="card stat-card h-100">
          <div class="card-body">
            <div class="stat-head">
              <span class="stat-title">
                <i class="fa-solid fa-bolt text-muted me-2"></i>Usuários ativos
              </span>
              <span class="stat-period">último mês</span>
            </div>
            <div class="stat-split">
              <div>
                <div class="stat-value">{{ numberBR(data.active_last_month.patient) }}</div>
                <div class="stat-label">Pacientes</div>
              </div>
              <div>
                <div class="stat-value">{{ numberBR(data.active_last_month.psy) }}</div>
                <div class="stat-label">Psis</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 col-md-6 col-xl-3">
        <div class="card stat-card h-100">
          <div class="card-body">
            <div class="stat-head">
              <span class="stat-title">
                <i class="fa-solid fa-bolt text-muted me-2"></i>Usuários ativos
              </span>
              <span class="stat-period">últimos 3 meses</span>
            </div>
            <div class="stat-split">
              <div>
                <div class="stat-value">{{ numberBR(data.active_last_3_months.patient) }}</div>
                <div class="stat-label">Pacientes</div>
              </div>
              <div>
                <div class="stat-value">{{ numberBR(data.active_last_3_months.psy) }}</div>
                <div class="stat-label">Psis</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 col-md-6 col-xl-3">
        <div class="card stat-card h-100">
          <div class="card-body">
            <div class="stat-head">
              <span class="stat-title">
                <i class="fa-regular fa-clock text-muted me-2"></i>Até a 1ª consulta
              </span>
              <span class="stat-period">média geral</span>
            </div>
            <div class="stat-split">
              <div>
                <div class="stat-value">
                  {{ data.avg_days_until_first_session }}
                  <small class="stat-unit">dias</small>
                </div>
                <div class="stat-label">Tempo médio</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row g-3">
      <div class="col-12 col-xl-8">
        <WidgetCard title="Cadastro de Usuários (mensal)">
          <ChartBar :data="registrationTimeline" :options="stackedOptions" />
        </WidgetCard>
      </div>
      <div class="col-12 col-xl-4">
        <WidgetCard title="Tipos de assinantes">
          <ChartDoughnut :data="subscriptionTypes" />
        </WidgetCard>
      </div>

      <div class="col-12">
        <WidgetCard title="Base ativa & Churn">
          <ChartLine :data="baseAndChurn" />
        </WidgetCard>
      </div>

      <div class="col-12 col-xl-6">
        <WidgetCard title="Pacientes com consultas">
          <ChartDoughnut :data="patientsWithAppointments" />
        </WidgetCard>
      </div>
      <div class="col-12 col-xl-6">
        <WidgetCard title="Pacientes com conta Stripe">
          <ChartDoughnut :data="patientsWithStripe" />
        </WidgetCard>
      </div>

      <div class="col-12 col-xl-6">
        <WidgetCard title="Visualizações de perfil (mensal)">
          <ChartLine :data="profileViewsMonthly" />
        </WidgetCard>
      </div>
      <div class="col-12 col-xl-6">
        <WidgetCard title="Visualizações de perfil (diário)">
          <ChartLine :data="profileViewsDaily" />
        </WidgetCard>
      </div>

      <div class="col-12 col-xl-6">
        <WidgetCard title="Cadastros diários (últ. 6m)">
          <ChartLine :data="dailyRegistrations" />
        </WidgetCard>
      </div>
      <div class="col-12 col-xl-6">
        <WidgetCard title="Cadastros diários — pacientes">
          <ChartLine :data="dailyPatientRegistrations" />
        </WidgetCard>
      </div>

      <div class="col-12 col-xl-6">
        <WidgetCard title="Consultas com sucesso (anual)">
          <ChartBar :data="yearlySuccessful" />
        </WidgetCard>
      </div>

      <div class="col-12 col-xl-6">
        <WidgetCard title="Top pacientes por consultas" :fixed="false">
          <div class="rank-list">
            <router-link
              v-for="(p, i) in topPatients"
              :key="'tp-' + p.id"
              :to="`${$paths.users.detail}/${p.id}`"
              class="rank-item"
            >
              <span class="rank-fill" :style="{ width: p.perc + '%' }"></span>
              <span class="rank-pos" :class="rankClass(i)">{{ i + 1 }}</span>
              <span class="rank-avatar">{{ initials(p.name) }}</span>
              <span class="rank-info">
                <span class="rank-name">{{ p.name }}</span>
                <span class="rank-email">{{ p.email }}</span>
              </span>
              <span class="rank-count">
                {{ numberBR(p.appointment_count) }}
                <small>consultas</small>
              </span>
            </router-link>
          </div>
        </WidgetCard>
      </div>
      <div class="col-12 col-xl-6">
        <WidgetCard title="Top psis por consultas" :fixed="false">
          <div class="rank-list">
            <router-link
              v-for="(p, i) in topPsys"
              :key="'ts-' + p.id"
              :to="`${$paths.psychologists.detail}/${p.id}`"
              class="rank-item"
            >
              <span class="rank-fill" :style="{ width: p.perc + '%' }"></span>
              <span class="rank-pos" :class="rankClass(i)">{{ i + 1 }}</span>
              <span class="rank-avatar">{{ initials(p.name) }}</span>
              <span class="rank-info">
                <span class="rank-name">{{ p.name }}</span>
                <span class="rank-email">{{ p.email }}</span>
              </span>
              <span class="rank-count">
                {{ numberBR(p.appointment_count) }}
                <small>consultas</small>
              </span>
            </router-link>
          </div>
        </WidgetCard>
      </div>
    </div>
  </div>
</template>

<script>
import {
  numberBR,
  COLORS,
  groupSeries,
  singleSeries,
  doughnut,
  baseOptions,
  dateTicks,
} from "@/utils/dashboardCharts";
import WidgetCard from "./WidgetCard.vue";

const typeLabel = (t) =>
  t === "user" ? "Pacientes" : t === "psychologist" ? "Psicólogos" : t;

export default {
  name: "DashUsers",
  components: { WidgetCard },
  props: { data: { type: Object, required: true } },
  data() {
    return {
      stackedOptions: {
        ...baseOptions,
        scales: {
          xAxes: [{ stacked: true, ticks: dateTicks }],
          yAxes: [{ stacked: true, ticks: { beginAtZero: true } }],
        },
      },
    };
  },
  computed: {
    registrationTimeline() {
      const rows = (this.data.registration_timeline || []).map((r) => ({
        ...r,
        type: typeLabel(r.type),
      }));
      return groupSeries(rows, "year_month", "type", "total");
    },
    subscriptionTypes() {
      return doughnut(this.data.subscription_types || [], "plano", "assinantes");
    },
    baseAndChurn() {
      const rows = this.data.base_and_churn || [];
      const labels = rows.map((r) => r.year_month);
      const series = [
        { key: "active_base", label: "Base ativa" },
        { key: "new_subscriptions", label: "Novas" },
        { key: "cancellations", label: "Cancelamentos" },
        { key: "net_growth", label: "Crescimento líquido" },
        { key: "churn_percent", label: "Churn %" },
      ];
      return {
        labels,
        datasets: series.map((s, i) => ({
          label: s.label,
          data: rows.map((r) => Number(r[s.key])),
          borderColor: COLORS[i % COLORS.length],
          backgroundColor: COLORS[i % COLORS.length],
          fill: false,
        })),
      };
    },
    profileViewsMonthly() {
      return singleSeries(
        this.data.profile_views_monthly || [],
        "year_month",
        "total_views",
        "Visualizações",
        2
      );
    },
    profileViewsDaily() {
      return singleSeries(
        this.data.profile_views_daily || [],
        "day",
        "total_views",
        "Visualizações",
        6
      );
    },
    patientsWithAppointments() {
      const d = this.data.patients_with_appointments || {};
      return {
        labels: ["Com consultas", "Sem consultas"],
        datasets: [
          {
            data: [d.with_appointments || 0, d.without_appointments || 0],
            backgroundColor: [COLORS[3], COLORS[9]],
          },
        ],
      };
    },
    patientsWithStripe() {
      const d = this.data.patients_with_stripe || {};
      return {
        labels: ["Com Stripe", "Sem Stripe"],
        datasets: [
          {
            data: [d.with_stripe || 0, d.without_stripe || 0],
            backgroundColor: [COLORS[2], COLORS[9]],
          },
        ],
      };
    },
    dailyRegistrations() {
      const rows = (this.data.daily_registrations || []).map((r) => ({
        ...r,
        type: typeLabel(r.type),
      }));
      return groupSeries(rows, "day", "type", "total");
    },
    dailyPatientRegistrations() {
      return singleSeries(
        this.data.daily_patient_registrations || [],
        "day",
        "total",
        "Pacientes",
        1
      );
    },
    yearlySuccessful() {
      return singleSeries(
        this.data.yearly_successful_appointments || [],
        "year",
        "total",
        "Consultas",
        0
      );
    },
    topPatients() {
      return this.withPerc(this.data.top_patients);
    },
    topPsys() {
      return this.withPerc(this.data.top_psys);
    },
  },
  methods: {
    numberBR,
    withPerc(list) {
      const rows = list || [];
      const max = rows.reduce(
        (m, r) => Math.max(m, Number(r.appointment_count) || 0),
        0
      );
      return rows.map((r) => ({
        ...r,
        perc: max ? Math.round(((Number(r.appointment_count) || 0) / max) * 100) : 0,
      }));
    },
    initials(name) {
      if (!name) return "?";
      const parts = String(name).trim().split(/\s+/);
      const first = parts[0] ? parts[0][0] : "";
      const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
      return (first + last).toUpperCase();
    },
    rankClass(i) {
      return i === 0 ? "gold" : i === 1 ? "silver" : i === 2 ? "bronze" : "";
    },
  },
};
</script>

<style scoped>
.rank-list {
  max-height: 380px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-right: 4px;
}
.rank-item {
  position: relative;
  flex: 0 0 auto;
  min-height: 52px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-radius: 10px;
  background-color: #f7f9fc;
  overflow: hidden;
  text-decoration: none;
  transition: background-color 0.15s;
}
.rank-item:hover {
  background-color: #eef2f8;
}
.rank-fill {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background: linear-gradient(90deg, rgba(42, 183, 206, 0.16), rgba(42, 183, 206, 0.04));
  z-index: 0;
}
.rank-pos,
.rank-avatar,
.rank-info,
.rank-count {
  position: relative;
  z-index: 1;
}
.rank-pos {
  flex: 0 0 24px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 0.72rem;
  font-weight: 700;
  color: #8896a6;
  background-color: #e6ebf2;
}
.rank-pos.gold {
  color: #7a5c00;
  background-color: #ffe08a;
}
.rank-pos.silver {
  color: #4a5560;
  background-color: #d7dee6;
}
.rank-pos.bronze {
  color: #7a4a24;
  background-color: #f0c9a6;
}
.rank-avatar {
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 0.72rem;
  font-weight: 700;
  color: #fff;
  background-color: #5b6bb5;
}
.rank-info {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}
.rank-name {
  font-size: 0.85rem;
  font-weight: 600;
  color: #12263f;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rank-email {
  font-size: 0.72rem;
  color: #8896a6;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rank-count {
  flex: 0 0 auto;
  text-align: right;
  font-size: 1.05rem;
  font-weight: 700;
  color: #123766;
  line-height: 1;
}
.rank-count small {
  display: block;
  font-size: 0.62rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #8896a6;
}

.stat-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.stat-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #495057;
}

.stat-period {
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #8896a6;
  background-color: #eef1f5;
  border-radius: 10px;
  padding: 2px 8px;
  white-space: nowrap;
}

.stat-split {
  display: flex;
  gap: 28px;
}

.stat-value {
  font-size: 1.6rem;
  font-weight: 700;
  color: #123766;
  line-height: 1.1;
}

.stat-unit {
  font-size: 0.9rem;
  font-weight: 600;
  color: #8896a6;
}

.stat-label {
  font-size: 0.75rem;
  color: #8896a6;
}
</style>
